import { useEffect, useRef, useState } from 'react';
import { taskRealtimeService, RealtimeSubscription } from '../services/taskRealtimeService';
import { useAuth } from '../context/AuthContext';
import { useCountry } from '../context/CountryContext';
import { Task } from '../types/task';

// Hook for real-time task updates for task takers
export const useTaskRealtime = () => {
  const [newTasks, setNewTasks] = useState<Task[]>([]);
  const [publishedTasks, setPublishedTasks] = useState<Task[]>([]);
  const subscriptionRef = useRef<RealtimeSubscription | null>(null);
  const { user } = useAuth();
  const { selectedCountry } = useCountry();

  useEffect(() => {
    // Clean up any existing subscription first
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }

    // Clear existing tasks when country/user changes
    setNewTasks([]);
    setPublishedTasks([]);

    if (!selectedCountry || !user) {
      return;
    }

    const handleTaskUpdate = (payload: any) => {
      if (payload.eventType === 'NEW_PUBLISHED') {
        // Extract task data - it should be in payload.new
        const taskData = payload.new;
        
        if (!taskData?.id) {
          return;
        }
        
        // Prevent duplicates by checking if task already exists
        setNewTasks(prev => {
          const exists = prev.some(task => task.id === taskData.id);
          if (exists) {
            return prev;
          }
          return [...prev, taskData];
        });
      } else if (payload.eventType === 'DRAFT_PUBLISHED') {
        // Handle draft to published transitions (not currently used)
        const taskData = payload.new;
        
        if (!taskData?.id) {
          return;
        }
        
        setPublishedTasks(prev => {
          const exists = prev.some(task => task.id === taskData.id);
          if (exists) {
            return prev;
          }
          return [...prev, taskData];
        });
      }
    };

    // Subscribe to task updates for the selected country, excluding the current user's tasks
    subscriptionRef.current = taskRealtimeService.subscribeToTasksByCountry(
      selectedCountry,
      handleTaskUpdate,
      user.id
    );

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [selectedCountry, user?.id]); // Only depend on stable values

  const clearNewTasks = () => setNewTasks([]);
  const clearPublishedTasks = () => setPublishedTasks([]);
  const clearAllTasks = () => {
    setNewTasks([]);
    setPublishedTasks([]);
  };

  return {
    newTasks,
    publishedTasks,
    allNewTasks: [...newTasks, ...publishedTasks],
    clearNewTasks,
    clearPublishedTasks,
    clearAllTasks
  };
};

// Hook for real-time task submission updates for task creators
export const useTaskSubmissionRealtime = (taskId: string | undefined) => {
  const [newSubmissions, setNewSubmissions] = useState<any[]>([]);
  const [updatedSubmissions, setUpdatedSubmissions] = useState<any[]>([]);
  const subscriptionRef = useRef<RealtimeSubscription | null>(null);

  useEffect(() => {
    if (!taskId) return;

    const handleSubmissionUpdate = (payload: any) => {
      if (payload.eventType === 'INSERT') {
        setNewSubmissions(prev => [...prev, payload.new]);
      } else if (payload.eventType === 'UPDATE') {
        setUpdatedSubmissions(prev => [...prev, payload.new]);
      }
    };

    subscriptionRef.current = taskRealtimeService.subscribeToTaskSubmissions(
      taskId,
      handleSubmissionUpdate
    );

    return () => {
      subscriptionRef.current?.unsubscribe();
    };
  }, [taskId]);

  const clearNewSubmissions = () => setNewSubmissions([]);
  const clearUpdatedSubmissions = () => setUpdatedSubmissions([]);

  return {
    newSubmissions,
    updatedSubmissions,
    clearNewSubmissions,
    clearUpdatedSubmissions
  };
};

// Hook for real-time submission status updates for task takers
export const useUserSubmissionRealtime = () => {
  const [statusUpdates, setStatusUpdates] = useState<any[]>([]);
  const subscriptionRef = useRef<RealtimeSubscription | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    const handleStatusUpdate = (payload: any) => {
      if (payload.eventType === 'STATUS_UPDATE') {
        setStatusUpdates(prev => [...prev, payload.new]);
      }
    };

    subscriptionRef.current = taskRealtimeService.subscribeToUserSubmissions(
      user.id,
      handleStatusUpdate
    );

    return () => {
      subscriptionRef.current?.unsubscribe();
    };
  }, [user]);

  const clearStatusUpdates = () => setStatusUpdates([]);

  return {
    statusUpdates,
    clearStatusUpdates
  };
};
